import React, { PureComponent } from "react";
import * as Icon from "react-feather";

import FilterGroup from "./FilterGroup";
import { newGroup, newRule, emptyTree, isEmpty, stripIds, assignIds, countRules } from "./filterTreeUtils";

/**
 * Reusable filter builder — D365-style nested AND/OR groups.
 *
 * Props:
 *   schema             : { fields: { [key]: {key, label, type, options?, group?} } }
 *   ctx                : passed to value inputs (e.g., { flowStates, teams, ... }) so picklist
 *                        option resolvers (`options: ctx => ctx.flowStates`) can find their lists
 *   value              : the current filter tree (root group with _id assigned)
 *   onChange           : (nextTree) => void
 *   onApply?           : optional — if provided, shows an "Apply" button. The parent decides
 *                        when filters are actually pushed to the server.
 *   hasPendingChanges? : when true, the Apply button is highlighted and a pending dot shows.
 *   onClear?           : optional — defaults to setting an empty tree
 */
class FilterBuilder extends PureComponent {
	static stripIds = stripIds;
	static assignIds = assignIds;
	static emptyTree = emptyTree;
	static isEmpty = isEmpty;
	static countRules = countRules;

	componentDidMount() {
		document.addEventListener("keydown", this.onKeyDown);
	}
	componentWillUnmount() {
		document.removeEventListener("keydown", this.onKeyDown);
	}

	onKeyDown = e => {
		// Cmd/Ctrl + Enter applies — only when focus is inside our root.
		if (e.key !== "Enter") return;
		if (!(e.metaKey || e.ctrlKey)) return;
		if (!this._rootRef || !this._rootRef.contains(document.activeElement)) return;
		if (!this.props.onApply) return;
		e.preventDefault();
		this.props.onApply();
	};

	onRootChange = nextRoot => {
		this.props.onChange(nextRoot || emptyTree());
	};

	onClear = () => {
		if (this.props.onClear) this.props.onClear();
		else this.props.onChange(emptyTree());
	};

	render() {
		const { schema, ctx, value, onApply, hasPendingChanges } = this.props;
		const tree = value && value.type === "group" ? value : emptyTree();
		const ruleCount = countRules(tree);

		return (
			<div className="filter-builder" ref={el => (this._rootRef = el)}>
				<div className="filter-builder__header">
					<div className="filter-builder__title">
						<Icon.Filter size={13} />
						<span>Filter rules</span>
						{ruleCount > 0 && (
							<span className="filter-builder__count">
								{ruleCount} {ruleCount === 1 ? "rule" : "rules"}
							</span>
						)}
						{hasPendingChanges && (
							<span className="filter-builder__pending" title="Unapplied changes">
								● Unapplied changes
							</span>
						)}
					</div>
					<div className="filter-builder__actions">
						<button type="button" className="filter-builder__btn-ghost" onClick={this.onClear}>
							<Icon.RotateCcw size={11} />
							<span>Clear</span>
						</button>
						{onApply && (
							<button
								type="button"
								className={
									"filter-builder__btn-primary " +
									(hasPendingChanges ? "filter-builder__btn-primary--pulse" : "")
								}
								onClick={onApply}
								title="Apply filters  (⌘/Ctrl + Enter)"
							>
								<Icon.Check size={11} />
								<span>Apply</span>
							</button>
						)}
					</div>
				</div>

				<FilterGroup
					group={tree}
					schema={schema}
					ctx={ctx}
					isRoot={true}
					depth={0}
					onChange={this.onRootChange}
				/>
			</div>
		);
	}
}

// Re-export utils so callers don't have to import from multiple files
export { newGroup, newRule, emptyTree, isEmpty, stripIds, assignIds, countRules };

export default FilterBuilder;
